import { useSyncExternalStore } from 'react';
import { ACCENT_COLORS, contrastRatio } from '@shared/color';
import { detectLocale, setLocale, type Locale } from './i18n';

export type ThemeMode = 'system' | 'light' | 'dark';
export type Accent = 'brand' | keyof typeof ACCENT_COLORS;
export const ACCENTS: readonly Accent[] = ['brand', 'blue', 'teal', 'green', 'violet', 'rose', 'orange'];
export { ACCENT_COLORS };

export interface UiPrefs {
  locale: Locale;
  theme: ThemeMode;
  accent: Accent;
  /** Desktop sidebar collapsed to an icon rail (per device). */
  sidebarCollapsed: boolean;
}

const KEY = 'resolveone.ui';
const DARK_SURFACE = '#171b21';

function load(): UiPrefs {
  const fallback: UiPrefs = { locale: detectLocale(), theme: 'system', accent: 'brand', sidebarCollapsed: false };
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? { ...fallback, ...(JSON.parse(raw) as Partial<UiPrefs>) } : fallback;
  } catch {
    return fallback; // storage blocked (private mode) — defaults still work
  }
}

let state: UiPrefs = typeof window === 'undefined' ? { locale: 'en', theme: 'system', accent: 'brand', sidebarCollapsed: false } : load();
const listeners = new Set<() => void>();

/** Emits CSS for one primary colour, honouring manual and system dark mode, with a dark-surface readability check. */
export function primaryCss(color: string): string {
  const darkOk = contrastRatio(color, DARK_SURFACE) >= 4.5;
  const light = `--primary: ${color}; --focus: ${color}66;`;
  return [
    `:root { --brand-button: ${color}; }`,
    `@media (prefers-color-scheme: light) { :root:not([data-theme='dark']) { ${light} } }`,
    `:root[data-theme='light'] { ${light} }`,
    darkOk ? `@media (prefers-color-scheme: dark) { :root:not([data-theme='light']) { ${light} } } :root[data-theme='dark'] { ${light} }` : '',
  ].join('\n');
}

function styleEl(id: string): HTMLStyleElement {
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el); // appended after branding → a personal accent wins over the workspace brand
  }
  return el;
}

function apply(p: UiPrefs): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (p.theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', p.theme);
  styleEl('user-accent').textContent = p.accent === 'brand' ? '' : primaryCss(ACCENT_COLORS[p.accent]);
  setLocale(p.locale);
}

export function getPrefs(): UiPrefs {
  return state;
}

export function setPrefs(patch: Partial<UiPrefs>): void {
  state = { ...state, ...patch };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* non-persistent session is fine */
  }
  apply(state);
  listeners.forEach((l) => l());
}

export function initPrefs(): void {
  apply(state);
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function usePrefs(): UiPrefs {
  return useSyncExternalStore(subscribe, getPrefs, getPrefs);
}
